import React, { useEffect, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { AssetPresentationsMenu } from 'features/Presentations/components/AssetPresentationsMenu'
import { AssetReportsButton } from 'features/Explorer/components/AssetReportsButton'
import { PortfolioPresentationButton } from 'features/Presentations/components/PortfolioPresentationButton'
import SecondaryNavCN from 'components/SecondaryNavCN/SecondaryNavCN'
import { CompositeLocationAvatar } from 'components/CompositeLocationAvatar'
import { Button } from 'components/ui/button'
import PortfolioActionSheet from './components/Portfolios/PortfolioActionSheet'
import RenderSubRoutes from 'components/RenderSubRoutes'
import SidebarPortfolioPartners from '../../components/Sidebars/SidebarPortfolioPartners'
import SidebarPortfolioAddLocation from 'components/Sidebars/SidebarPortfolioAddLocation'
import getPortfolioRoutes from './getRoutes/getPortfolioRoutes'
import getPortfolioPartnerRoutes from './getRoutes/getPortfolioPartnerRoutes'

import routes from 'constants/routes'
import useMediaQuery from 'hooks/useMediaQuery'
import useCapabilities from 'hooks/useCapabilities'
import { findPartnerEntry } from 'utils/capabilities'
import * as _ from 'styles/helpers'
import { initializePortfolios, getUpdatedPortfolio } from 'slices/portfolioSlice'
import { initializeUsers } from 'slices/usersSlice'
import {
	fetchEvaluationByPortfolioId,
	createEvaluationFromPortfolio,
	syncEvaluationWithPortfolio,
} from 'slices/evaluationSlice'
import { formatSyncSummary } from 'utils/portfolioSync'

const Portfolios_ShowCN = () => {
	const dispatch = useDispatch()
	const params = useParams()
	const { id } = params

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [backTitle, setBackTitle] = useState(null)
	const [openEditSheet, setOpenEditSheet] = useState(false)

	const navigate = useNavigate()
	const [linkedEvaluation, setLinkedEvaluation] = useState<any>(null)
	const [pushInFlight, setPushInFlight] = useState(false)
	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ title, message, variant }: { title: string; message: string; variant: 'success' | 'destructive' }) =>
		variant === 'success' ? toast.success(message, { title }) : toast.error(message, { title })

	const currentRoute = useLocation()
	const getBackLink = () => {
		if (currentRoute.state) {
			if (backTitle === null) {
				setBackTitle(currentRoute.state.backButtonTitle)
			}
			return { title: currentRoute.state.backButtonTitle, link: currentRoute.state.backButtonLink }
		}
		return { title: 'Portfolios', link: routes.PORTFOLIOS }
	}

	const pathArray = currentRoute.pathname.split('/')
	const isAddLocationsPage = pathArray[pathArray.length - 1] === 'addlocations'

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin
	const capabilities = useCapabilities()

	const { isLoading, portfolio, isInitialized } = useSelector((state: any) => ({
		isLoading: state.portfolios.isLoading,
		isInitialized: state.portfolios.isInitialized,
		portfolio: state.portfolios.entities[id],
	}))

	const { success: userListInitialized } = useSelector((state: any) => ({
		success: state.userList.isInitialized,
	}))

	useEffect(() => {
		if (!isInitialized) {
			dispatch(initializePortfolios())
		} else if (id) {
			// The thunk takes the portfolio id as its argument. Calling it with
			// none produced `GET /api/portfolios/undefined`, which 500s on the
			// ObjectId cast and left the whole screen blank when a portfolio tab
			// was opened directly rather than navigated to.
			dispatch(getUpdatedPortfolio(id))
		}
		if (!userListInitialized) {
			dispatch(initializeUsers())
		}
	}, [dispatch, id, isInitialized, userListInitialized])

	useEffect(() => {
		if (!id) return
		;(dispatch as any)(fetchEvaluationByPortfolioId(id)).then((result: any) => {
			if (result?.payload !== undefined) {
				setLinkedEvaluation(result.payload)
			}
		})
	}, [dispatch, id])

	const openEdit = () => {
		setOpenEditSheet(true)
	}

	const locationCount = portfolio?.locations?.length || 0
	const hasLinkedEvaluation = Boolean(linkedEvaluation?._id)
	// Pushing with no linked evaluation CREATES one, which community accounts
	// may not do (MYD-633). Syncing one that already exists is not a create.
	const canPushToEvaluation = hasLinkedEvaluation || capabilities.canCreateAssets

	const handlePushToEvaluation = async () => {
		if (!portfolio || locationCount === 0) return
		setPushInFlight(true)
		try {
			if (hasLinkedEvaluation) {
				const result: any = await (dispatch as any)(syncEvaluationWithPortfolio(linkedEvaluation._id))
				if (result?.meta?.requestStatus === 'fulfilled') {
					showToast({
						title: 'Evaluation synced',
						message: formatSyncSummary(result.payload || {}),
						variant: 'success',
					})
				} else {
					showToast({
						title: 'Sync failed',
						message:
							typeof result?.payload === 'string'
								? result.payload
								: 'Could not sync the evaluation. Please try again.',
						variant: 'destructive',
					})
				}
			} else {
				const result: any = await (dispatch as any)(
					createEvaluationFromPortfolio({
						portfolioId: portfolio._id,
						name: portfolio.assetName || 'Untitled Evaluation',
						description: portfolio.description,
					}),
				)
				if (result?.meta?.requestStatus === 'fulfilled') {
					navigate(`${routes.EVALUATIONS}/${result.payload._id}`)
				} else if (result?.payload?.status === 409 && result.payload.existingEvaluationId) {
					// Portfolio got linked by another tab/session between mount and click — recover gracefully.
					setLinkedEvaluation({ _id: result.payload.existingEvaluationId, name: result.payload.existingEvaluationName })
					showToast({
						title: 'Evaluation already exists',
						message: `"${result.payload.existingEvaluationName}" is already linked to this portfolio. The button now reads Sync Evaluation — click it again to bring the sites up to date.`,
						variant: 'destructive',
					})
				} else {
					showToast({
						title: 'Could not create evaluation',
						message: 'Failed to create the evaluation. Please try again.',
						variant: 'destructive',
					})
				}
			}
		} finally {
			setPushInFlight(false)
		}
	}

	const partners = portfolio?.partners
	const assetOwner = portfolio?.owner

	if (!portfolio || isLoading) {
		return null
	}

	// A partner's own entry carries the access level this portfolio granted
	// them, which decides whether they see Financials and Reports. Rows written
	// before access levels existed read as 'full'.
	const myPartnerEntry = findPartnerEntry(portfolio, userInfo._id)
	const myAccessLevel = myPartnerEntry?.accessLevel

	let subRoutes: any[] = []
	if (assetOwner?._id === userInfo._id) {
		subRoutes = getPortfolioRoutes(id, capabilities)
	} else {
		subRoutes = getPortfolioPartnerRoutes(id, capabilities, myAccessLevel)
	}

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2 flex items-center gap-3">
					<CompositeLocationAvatar
						locations={(portfolio?.locations || [])
							.map((item: any) => item?.location)
							.filter(Boolean)
							.map((loc: any) => ({
								imageUrl: loc?.image,
								name: loc?.assetName || '',
							}))}
						size="sm"
						className="rounded-full"
					/>
					{portfolio?.owner?._id === userInfo?._id ? (
						<button
							type="button"
							onClick={openEdit}
							className="font-semibold text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
						>
							{portfolio?.assetName}
						</button>
					) : (
						<span className="font-semibold text-sm text-gray-800">{portfolio?.assetName}</span>
					)}
					{portfolio?.description && <span className="text-sm text-gray-500">{portfolio.description}</span>}
					<span className="text-sm text-gray-500">Portfolio Type: {portfolio?.portfolioType}</span>
					<div className="ml-auto flex items-center gap-2">
						{/* Decks with a story about this portfolio (MYD-497). Renders nothing when there are none. */}
						{/* Reports from a template, narrowed to this portfolio (MYD-598). */}
						<AssetReportsButton type="portfolio" entityId={id} name={portfolio?.assetName} />
						<AssetPresentationsMenu scope="portfolio" entityId={id} />
						{/* A review deck: the overview, then each location (MYD-507). */}
						<PortfolioPresentationButton portfolio={id && portfolio ? { entityId: id, name: portfolio.assetName ?? '' } : null} />
						{canPushToEvaluation && (
							<Button
								type="button"
								variant="primary"
								isOutline
								onClick={handlePushToEvaluation}
								disabled={pushInFlight || locationCount === 0}
								title={locationCount === 0 ? 'Add locations to the portfolio before pushing to an evaluation' : undefined}
							>
								{pushInFlight ? 'Working...' : hasLinkedEvaluation ? 'Sync Evaluation' : 'Push to Evaluation'}
							</Button>
						)}
					</div>
				</div>

				<SecondaryNavCN links={subRoutes} backLink={getBackLink()} sourceId={null} sourceName="Portfolios" />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<RenderSubRoutes routes={subRoutes} state={currentRoute.state} />
					</main>

					<RightPanel storageKey="portfolio" label="Portfolio panel" className="overflow-y-auto">
						{portfolio && (
							<>
								{isAddLocationsPage ? (
									<SidebarPortfolioAddLocation />
								) : (
									<SidebarPortfolioPartners
										assetOwner={assetOwner}
										assetPartners={partners}
										assetName={portfolio?.assetName}
										userId={userInfo._id}
									/>
								)}
							</>
						)}
					</RightPanel>
				</div>
			</div>

			<PortfolioActionSheet
				portfolio={portfolio}
				open={openEditSheet}
				onClose={() => setOpenEditSheet(false)}
				isOwner={portfolio?.owner?._id === userInfo?._id}
				showDelete={false}
			/>

		</div>
	)
}

export default Portfolios_ShowCN
